import dayjs from "dayjs";
import { useState } from "react";
import Box from "@mui/material/Box";
import Loader from "./Display/Loader";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";
import { CardTitle } from "./theme/texts";
import Avatar from "@mui/material/Avatar";
import TextInput from "./Inputs/TextInput";
import NoDataDiv from "./Display/NoDataDiv";
import { Theme } from "../data/style/Theme";
import { useNavigate } from "react-router-dom";
import * as routes from "../data/links/routes";
import { deleteComment } from "../api/comments";
import { useAuth } from "../context/AuthContext";
import { errorTexts } from "../data/assets/texts";
import relativeTime from "dayjs/plugin/relativeTime";
import useScreenSize from "../context/ScreenContext";
import { unicodeToEmoji } from "../helpers/functions";
import { useNotifications } from "../context/NotificationContext";
import { getWorkoutComments, postComment } from "../api/workouts";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  scrollBar,
  SpacedRow,
  MainText,
  CenteredRow,
  AllCenteredColumn,
} from "../data/style/Style";

const Comments = (props) => {
  let { workoutId } = props;
  dayjs.extend(relativeTime);
  const navigate = useNavigate();
  const client = useQueryClient();
  const [comment, setComment] = useState("");
  const { enableNotification } = useNotifications();
  const { mobileDevice } = useScreenSize();

  const { data, isPending, refetch } = useQuery({
    queryKey: ["workout", parseInt(workoutId), "comments"],
    queryFn: ({ signal }) => getWorkoutComments({ signal, workoutId }),
  });

  const { mutate } = useMutation({
    mutationFn: postComment,
    onSuccess: () => {
      refetch();
      client.invalidateQueries({
        queryKey: ["workout", parseInt(workoutId)],
      });
      setComment("");
    },
    onError: (error) => {
      if (error.response.status === 400) {
        enableNotification(error.response.data.message, "error");
        return;
      }
      if (error.response.status === 403) {
        navigate(routes.LOGIN);
        return;
      }

      if (error.response.status >= 500) {
        enableNotification(errorTexts.system_error, "error");
      }
    },
  });

  let handlePostComment = () => mutate({ workoutId, comment });

  let handleCommentChange = (e) => {
    if (e.target.value.length < 250) setComment(e.target.value);
  };

  let commentContent = (
    <AllCenteredColumn height={"100%"}>
      <Loader height={"100%"} />
    </AllCenteredColumn>
  );

  if (!isPending) {
    if (data.length > 0) {
      commentContent = (
        <>
          {data.map((item) => (
            <Comment item={item} key={item.id} />
          ))}
        </>
      );
    } else {
      commentContent = (
        <NoDataDiv
          icon={<span style={{ fontSize: "3rem" }}>💬</span>}
          height={"100%"}
          header={"No comments yet!"}
          text={"Be the first to share your thoughts! "}
        />
      );
    }
  }

  return (
    <Stack
      gap={1}
      overflow={"auto"}
      minWidth={mobileDevice ? "275px" : "350px"}
      height={"95vh"}
      sx={scrollBar}
      p={1}
    >
      {!isPending && (
        <CardTitle
          subTitle={"Community"}
          mainTitle={
            <>
              Comments&nbsp;
              <span style={{ color: Theme.palette.orange, fontSize: "0.8rem" }}>
                ({data.length})
              </span>
            </>
          }
        />
      )}
      <SpacedRow width={"100%"} mb={2} gap={2} alignItems={"center"}>
        <Box width={"85%"}>
          <TextInput
            multi={true}
            rows={1}
            placeholder={"Write a comment..."}
            val={comment}
            onChange={handleCommentChange}
          />
        </Box>
        <AllCenteredColumn
          sx={{
            marginTop: 1,
            cursor: "pointer",
            borderRadius: 3,
            background: Theme.palette.card,
            border: 1,
            borderColor: Theme.palette.borderLight,

            "&:hover": {
              background: "#141414",
            },
          }}
          p={1}
          onClick={handlePostComment}
        >
          <span style={{ color: Theme.palette.muted }}>→</span>
        </AllCenteredColumn>
      </SpacedRow>
      {commentContent}
    </Stack>
  );
};
export default Comments;

const Comment = ({ item }) => {
  const { profile } = useAuth();
  const [commentPopper, setCommentPopper] = useState(false);
  const client = useQueryClient();
  const handleClick = () => {
    setCommentPopper((prev) => !prev);
  };
  const { refetch } = useQuery({
    queryKey: ["comment", "delete"],
    queryFn: ({ signal }) => deleteComment({ signal, commentId: item.id }),
    enabled: false,
  });
  return (
    <Stack
      sx={{
        border: 1,
        borderColor: Theme.palette.border,
        background: Theme.palette.card,
        borderRadius: 3,
        p: 1,
      }}
      width={"95%"}
      gap={0.5}
    >
      <SpacedRow alignItems={"center"}>
        <CenteredRow gap={1}>
          <Avatar
            sx={{ width: 30, height: 30 }}
            alt={item.name}
            src={item.user_photo}
          />
          <MainText
            sx={{
              color:
                item.user === profile.user &&
                Theme.palette.orange + " !important",
            }}
          >
            {item.name}
          </MainText>
        </CenteredRow>
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          <MainText
            sx={{ color: Theme.palette.muted + " !important" }}
            fontSize={"0.8rem"}
          >
            {dayjs(item.created).fromNow()}
          </MainText>
          <Box position={"relative"}>
            <MainText
              onClick={handleClick}
              sx={{
                color: Theme.palette.muted + " !important",
                cursor: "pointer",
              }}
              fontSize={"0.8rem"}
            >
              ⋯
            </MainText>
            <Fade unmountOnExit in={commentPopper} timeout={500}>
              <Box
                sx={{
                  right: -15,
                  borderRadius: 3,
                  p: 1,
                  position: "absolute",
                  border: 1,
                  borderColor: Theme.palette.borderLight,
                  background: Theme.palette.card,
                }}
              >
                <MainText
                  onClick={() => {
                    console.log(item.wod);
                    refetch();
                    setTimeout(() => {
                      client.invalidateQueries({
                        queryKey: ["workout", parseInt(item.wod)],
                      });
                      client.invalidateQueries({
                        queryKey: ["workout", parseInt(item.wod), "comments"],
                      });
                    }, 100);
                  }}
                  fontSize={"0.8rem"}
                  sx={{
                    color: Theme.palette.down + " !important",
                    cursor: "pointer",
                  }}
                >
                  Delete
                </MainText>
              </Box>
            </Fade>
          </Box>
        </Stack>
      </SpacedRow>
      <MainText>{unicodeToEmoji(item.body)}</MainText>
    </Stack>
  );
};
